import { z } from 'zod';
import { displayNameSchema } from './auth.js';

/**
 * A room id as it travels on the wire. Rooms are in-memory objects on a worker with a lifetime
 * of minutes and no table of their own (see `match_results.room_id`), so this is a bounded
 * opaque string rather than a uuid.
 */
export const roomIdSchema = z.string().trim().min(1).max(64);

/**
 * Matchmaking join request. The client asks for a room; the matchmaker answers with a worker
 * address and a short-lived ticket. A client may *request* a specific room (to play with a
 * friend) but never asserts which worker serves it.
 */
export const joinRequestSchema = z.object({
  displayName: displayNameSchema,
  /** Optional: join a specific room, e.g. from a share link. Ignored if the room is full. */
  roomId: roomIdSchema.optional(),
  /** Optional region hint; the matchmaker still decides, this only biases the choice. */
  region: z.string().trim().min(2).max(16).optional(),
});

/**
 * Asking to WATCH a room rather than to play in it.
 *
 * The room is required and there is no fallback, which is the difference between this and
 * `joinRequestSchema`: quick play means "put me somewhere with people in it", and there is no
 * equivalent wish for a spectator — you watch a room you picked, or you watch nothing. It also
 * keeps a spectator from ever *creating* or *waking* a room, which is the point (see
 * `placeSpectator`).
 *
 * No display name, because a spectator has no blob for one to go over and appears in nobody's
 * frame. What comes back is the same address and the same kind of signed ticket a player gets —
 * with one claim different — so the client opens the socket it always did.
 */
export const spectateRequestSchema = z.object({
  roomId: roomIdSchema,
});

export const joinResponseSchema = z.object({
  roomId: z.string(),
  /** WebSocket URL of the worker hosting the room. */
  wsUrl: z.string(),
  /** Short-lived ticket presented in the WebSocket `hello` message. */
  ticket: z.string(),
});

/**
 * Asking the API server for a WebSocket ticket. The room is required here — a ticket is bound
 * to one room, so there is no such thing as an unaddressed one.
 */
export const wsTicketRequestSchema = z.object({
  roomId: roomIdSchema,
});

export type JoinRequest = z.infer<typeof joinRequestSchema>;
export type SpectateRequest = z.infer<typeof spectateRequestSchema>;
export type JoinResponse = z.infer<typeof joinResponseSchema>;
export type WsTicketRequest = z.infer<typeof wsTicketRequestSchema>;
